import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { chunkedUpload } from "@/lib/chunkedUpload";
import { isUploaded } from "@/lib/videoStream";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import Classification, { CLASSIFICATIONS } from "@/components/Classification";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UploadCloud, RefreshCw, Archive, RotateCcw, Film, CheckCircle2, AlertCircle } from "lucide-react";

const MAX_MB = 500;
const TEXT_FIELDS = [
  ["title", "Video Title"], ["product", "Product"], ["product_family", "Product Family"],
  ["model", "Model"], ["sku", "SKU"], ["problem", "Problem / Symptom"],
  ["procedure", "Troubleshooting Procedure"], ["step", "Exact Troubleshooting Step"],
  ["linked_articles", "Linked Article IDs (comma separated)"], ["escalation_point", "Escalation Instructions"],
  ["version", "Version"], ["last_reviewed", "Review Date"],
  ["video_url", "External video URL (optional — used instead of uploading a file)"],
];
const empty = {
  title: "", description: "", product: "", product_family: "", model: "", sku: "", problem: "",
  procedure: "", step: "", linked_articles: "", classification: "INTERNAL STAFF ONLY",
  safety_warning: "", escalation_point: "", customer_safe: "No", version: "", last_reviewed: "", status: "Draft", video_url: "",
};

export default function VideosManager() {
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);
  const replaceRef = useRef();
  const [replaceId, setReplaceId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | success | error
  const retryRef = useRef(null);

  const load = () => api.get("/content/videos").then((r) => setVideos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setFile(null); setUploadState("idle"); setProgress(0); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, v[k] ?? empty[k]])) });
    setFile(null); setUploadState("idle"); setProgress(0); setOpen(true);
  };

  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const validateFile = (f) => {
    if (!f) return true;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`File exceeds the ${MAX_MB} MB limit.`); return false; }
    return true;
  };

  const runUpload = async ({ file, targetId, metadata }) => {
    setUploadState("uploading"); setProgress(0);
    retryRef.current = { file, targetId, metadata, uploadId: retryRef.current?.uploadId };
    try {
      const { video } = await chunkedUpload(file, {
        metadata, targetId, uploadId: retryRef.current?.uploadId,
        onInit: (id) => { retryRef.current = { file, targetId, metadata, uploadId: id }; },
        onProgress: setProgress,
      });
      retryRef.current = null;
      setUploadState("success");
      return video;
    } catch (e) {
      setUploadState("error");
      e.uploadHandled = true;
      toast.error("Upload failed — you can retry to resume.");
      throw e;
    }
  };

  const save = async () => {
    if (file && !validateFile(file)) return;
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/content/videos/${editing.id}`, form);
        if (file) await runUpload({ file, targetId: editing.id });
      } else if (file) {
        await runUpload({ file, metadata: form });
      } else {
        const fd = new FormData();
        fd.append("metadata", JSON.stringify(form));
        await api.post("/videos", fd);
      }
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) {
      if (!e.uploadHandled) toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const retryUpload = async () => {
    if (!retryRef.current) return;
    setBusy(true);
    try {
      await runUpload(retryRef.current);
      if (editing) await api.put(`/content/videos/${editing.id}`, form);
      toast.success("Upload complete");
      setOpen(false); load();
    } catch (_) { /* stays in error state */ } finally { setBusy(false); }
  };

  const doReplace = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !validateFile(f) || !replaceId) return;
    setBusy(true);
    try {
      retryRef.current = null;
      await runUpload({ file: f, targetId: replaceId });
      toast.success("Video file replaced (article links kept)");
      load();
    } catch (_) { /* handled */ } finally {
      setBusy(false); setReplaceId(null); if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const setStatus = async (v, status) => {
    try { await api.put(`/content/videos/${v.id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/content/videos/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-bold text-slate-900">Videos ({videos.length})</h3>
          <p className="text-xs text-slate-500">Upload MP4 (recommended), WebM, MOV or OGG directly. Large files upload in resumable chunks. A video is customer-facing only when Customer Safe = Yes.</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button className="bg-mlily-blue hover:bg-mlily-bluehover gap-1" onClick={openNew} data-testid="add-video-btn"><Plus className="w-4 h-4" /> Upload / add video</Button>
          <span className="text-xs font-semibold text-slate-600" data-testid="max-size-label">Maximum video size: {MAX_MB} MB</span>
        </div>
      </div>

      {uploadState === "uploading" && (
        <Card className="p-3" data-testid="global-upload-progress">
          <div className="flex items-center justify-between text-sm text-slate-700 mb-1">
            <span className="flex items-center gap-2"><UploadCloud className="w-4 h-4 text-mlily-blue" /> Uploading…</span>
            <span className="font-semibold">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </Card>
      )}

      <input ref={replaceRef} type="file" accept="video/*" className="hidden" onChange={doReplace} data-testid="replace-file-input" />

      <div className="grid gap-2">
        {videos.map((v) => (
          <Card key={v.id} className="p-3" data-testid={`admin-video-${v.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                  <Film className="w-4 h-4 text-violet-500" /> {v.title || v.id}
                </div>
                <div className="text-xs text-slate-400 flex gap-2 items-center flex-wrap mt-1">
                  <Badge variant="outline" className="text-[10px]">{v.status}</Badge>
                  {isUploaded(v) ? <Badge className="bg-violet-500 text-[10px]">Uploaded file</Badge> : v.video_url ? <Badge variant="outline" className="text-[10px]">External link</Badge> : <Badge variant="outline" className="text-[10px]">No file</Badge>}
                  <Badge className={v.customer_safe === "Yes" ? "bg-emerald-500 text-[10px]" : "bg-slate-400 text-[10px]"}>{v.customer_safe === "Yes" ? "Customer safe" : "Not customer-facing"}</Badge>
                  {v.uploaded_by && <span>by {v.uploaded_by}</span>}
                  {v.updated_at && <span>· {v.updated_at.slice(0, 10)}</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" title="Replace file" onClick={() => { setReplaceId(v.id); replaceRef.current?.click(); }} data-testid={`replace-video-${v.id}`}><RefreshCw className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(v)} data-testid={`edit-video-${v.id}`}><Pencil className="w-4 h-4" /></Button>
                {v.status === "Retired" ? (
                  <Button variant="ghost" size="icon" title="Restore" className="text-emerald-600" onClick={() => setStatus(v, "Approved")} data-testid={`restore-video-${v.id}`}><RotateCcw className="w-4 h-4" /></Button>
                ) : (
                  <Button variant="ghost" size="icon" title="Retire" className="text-amber-600" onClick={() => setStatus(v, "Retired")} data-testid={`retire-video-${v.id}`}><Archive className="w-4 h-4" /></Button>
                )}
                <Button variant="ghost" size="icon" className="text-red-500" title="Delete" onClick={() => setDelId(v.id)} data-testid={`delete-video-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </Card>
        ))}
        {videos.length === 0 && <p className="text-sm text-slate-400">No videos yet.</p>}
      </div>

      {/* Add / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit video" : "Upload / add video"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border-2 border-dashed border-slate-300 p-4">
              <Label className="flex items-center gap-2 mb-2"><UploadCloud className="w-4 h-4" /> {editing ? "Replace video file (optional)" : "Video file"}</Label>
              <Input type="file" accept="video/mp4,video/webm,video/quicktime,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="video-file-input" />
              <p className="text-xs text-slate-500 mt-2">Maximum video size: {MAX_MB} MB. MP4 recommended (WebM, MOV, OGG also supported). {editing && "Leave empty to keep the current file."}</p>
              {editing && isUploaded(editing) && <p className="text-xs text-slate-400 mt-1">Current file: {editing.original_filename}</p>}
            </div>
            {TEXT_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label>
                <Input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`vfield-${k}`} /></div>
            ))}
            <div className="space-y-1.5"><Label>Description / Caption</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="vfield-description" /></div>
            <div className="space-y-1.5"><Label>Safety Warning</Label>
              <Textarea rows={2} value={form.safety_warning} onChange={(e) => set("safety_warning", e.target.value)} data-testid="vfield-safety" /></div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Classification</Label>
                <Select value={form.classification} onValueChange={(v) => set("classification", v)}>
                  <SelectTrigger data-testid="vfield-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSIFICATIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Customer Safe?</Label>
                <Select value={form.customer_safe} onValueChange={(v) => set("customer_safe", v)}>
                  <SelectTrigger data-testid="vfield-customer-safe"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="No">No</SelectItem><SelectItem value="Yes">Yes</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="vfield-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Approved">Approved</SelectItem><SelectItem value="Retired">Retired</SelectItem></SelectContent>
                </Select></div>
            </div>
            {form.classification !== "CUSTOMER SAFE" && form.customer_safe === "Yes" && (
              <p className="text-xs text-amber-600">Note: classification is not "Customer Safe" — confirm this video is appropriate to share with customers.</p>
            )}
            {uploadState !== "idle" && (
              <div className="rounded-lg border border-slate-200 p-3" data-testid="upload-progress">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-2">
                    {uploadState === "uploading" && <><UploadCloud className="w-4 h-4 text-mlily-blue" /> Uploading…</>}
                    {uploadState === "success" && <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Upload complete</>}
                    {uploadState === "error" && <><AlertCircle className="w-4 h-4 text-red-600" /> Upload failed</>}
                  </span>
                  <span className="font-semibold text-slate-700">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
                {uploadState === "error" && (
                  <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={retryUpload} disabled={busy} data-testid="retry-upload-btn">
                    <RefreshCw className="w-4 h-4" /> Retry / resume upload
                  </Button>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-mlily-blue hover:bg-mlily-bluehover" disabled={busy} onClick={save} data-testid="save-video-btn">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this video?</AlertDialogTitle><AlertDialogDescription>This permanently removes the metadata reference. Consider Retire instead.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={del} data-testid="confirm-delete-video-btn">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
